/**
 * PaywallContext — global open/close for the Paywall modal.
 *
 * Any component can call:
 *   const { openPaywall } = usePaywall();
 *   openPaywall('analytics');   // reason key from REASON_COPY in Paywall.jsx
 *
 * The provider mounts a single <Paywall variant="modal" /> at App root so
 * we don't stack duplicates from every consumer.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Paywall } from '../components/Paywall';

const PaywallCtx = createContext({
  openPaywall: () => {},
  closePaywall: () => {},
  isOpen: false,
});

export function PaywallProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('default');

  const openPaywall = useCallback((r = 'default') => {
    setReason(r);
    setOpen(true);
  }, []);
  const closePaywall = useCallback(() => setOpen(false), []);

  // iter360 · Listen for the global `flyt:open-paywall` event so any
  // code path outside the React tree (e.g. checkoutRetry.js on native
  // iOS) can open the StoreKit paywall modal without dragging in the
  // context hook. Fires on native iOS whenever a caller would have
  // opened Stripe — routes them to the Apple pop-up instead.
  useEffect(() => {
    const onOpen = (e) => {
      const r = (e?.detail?.reason) || 'default';
      setReason(r);
      setOpen(true);
    };
    window.addEventListener('flyt:open-paywall', onOpen);
    return () => window.removeEventListener('flyt:open-paywall', onOpen);
  }, []);

  const value = useMemo(
    () => ({ openPaywall, closePaywall, isOpen: open }),
    [openPaywall, closePaywall, open]
  );

  return (
    <PaywallCtx.Provider value={value}>
      {children}
      {open && <Paywall variant="modal" reason={reason} onClose={closePaywall} />}
    </PaywallCtx.Provider>
  );
}

export function usePaywall() {
  return useContext(PaywallCtx);
}
